// ============================================================================
// BrandHead.jsx — applies the live config/brand doc to the document <head> at
// runtime, for JS-executing clients (real browsers). Mounted once in AppShell.
//
// This makes the browser TAB TITLE and the OG/Twitter/meta tags reflect the
// current business model without a redeploy — for anyone whose browser runs JS.
//
// ⚠ It does NOT fix raw social scrapers (Facebook/iMessage/Slack) that read the
// STATIC public/index.html without executing JS. Those still need the static
// file (and the seo-layer) rebuilt from config/brand — see brandTouchpoints.js.
//
// No react-helmet dependency (there is no root HelmetProvider); we set the tags
// directly. It only sets the site-wide DEFAULT — a route that renders its own
// <title>/meta still wins on that route.
// ============================================================================
import { useEffect } from 'react';
import { subscribeBrand } from '../firebase-modular/firebase-brand';

function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    return window.location.origin + (pathOrUrl.startsWith('/') ? '' : '/') + pathOrUrl;
  } catch {
    return pathOrUrl;
  }
}

// The site is reachable on both www. and non-www hosts (both return 200), which
// makes Google treat every page as a duplicate with no chosen canonical. We fix
// that by emitting a self-referencing <link rel="canonical"> on EVERY route,
// always normalized to the single canonical origin below. This consolidates the
// two hosts and gives each route its own canonical URL (instead of all routes
// inheriting the homepage's og:url from the static index.html).
// NOTE: keep this host in sync with seo-layer SITE_URL + sitemap.xml.
const CANONICAL_ORIGIN = 'https://lextrackai.com';

function setCanonical() {
  try {
    // Path only (drop query/hash) so ?debug=1 etc. don't fork the canonical.
    const canonicalHref = CANONICAL_ORIGIN + window.location.pathname;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalHref);
    // Keep og:url aligned with the canonical (static index.html hardcodes the
    // homepage URL on every route, which contradicts the per-route canonical).
    setMeta('property', 'og:url', canonicalHref);
  } catch {
    /* no-op */
  }
}

export default function BrandHead() {
  // Self-referencing canonical, kept correct across SPA navigation. Googlebot
  // loads each URL fresh (so mount alone suffices for indexing); the popstate
  // listener keeps it right for real users sharing a deep-linked page.
  useEffect(() => {
    setCanonical();
    const onNav = () => setCanonical();
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);

  useEffect(() => {
    const unsub = subscribeBrand((b) => {
      const seo = b.seo || {};
      if (seo.tabTitle) document.title = seo.tabTitle;
      setMeta('name', 'description', seo.metaDescription);
      setMeta('property', 'og:title', seo.ogTitle);
      setMeta('property', 'og:description', seo.ogDescription);
      setMeta('property', 'og:image', absoluteUrl(seo.ogImageUrl));
      setMeta('name', 'twitter:title', seo.twitterTitle);
      setMeta('name', 'twitter:description', seo.twitterDescription);
      setMeta('name', 'twitter:image', absoluteUrl(seo.ogImageUrl));
      if (seo.twitterHandle) {
        setMeta('name', 'twitter:site', seo.twitterHandle);
        setMeta('name', 'twitter:creator', seo.twitterHandle);
      }
      // Favicon — swap at runtime when it's been replaced with an absolute URL.
      const fav = b.assets && b.assets.faviconUrl;
      if (fav && /^https?:\/\//i.test(fav)) {
        let link = document.head.querySelector('link[rel="icon"]');
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'icon');
          document.head.appendChild(link);
        }
        link.setAttribute('href', fav);
      }
    });
    return unsub;
  }, []);
  return null;
}
